import React, { useEffect, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem, CircularProgress } from "@mui/material";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { updateEdge } from "../../api/edgeApi";
import { getTenantLocations, removeEdgeFromLocation } from "../../api/tenantApi";

const NO_LOCATION = "__none__";

// Backend rejects a location outside the edge's own tenant (_check_location_in_tenant), so the
// pick list is that tenant's locations only. Clearing back to "Lokasyonsuz" mirrors TenantList.js's
// handleConfirmRemoveEdge (removeEdgeFromLocation), since PUT /edges/{id} has no way to null it out.
export default function MoveEdgeDialog({ open, edge, accessToken, onClose, onSaved }) {
  const { t } = useTranslation();
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [targetLocationId, setTargetLocationId] = useState(NO_LOCATION);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !edge) return;
    setTargetLocationId(edge.location_id || NO_LOCATION);
    if (accessToken && edge.tenant_id) {
      setLoadingLocations(true);
      getTenantLocations(accessToken, edge.tenant_id)
        .then((data) => setLocations(data.items || data || []))
        .catch(() => setLocations([]))
        .finally(() => setLoadingLocations(false));
    }
  }, [open, edge, accessToken]);

  const handleMove = async () => {
    setSubmitting(true);
    try {
      let updated;
      if (targetLocationId === NO_LOCATION) {
        updated = await removeEdgeFromLocation(accessToken, edge.id);
      } else {
        const payload = {
          edgeId: edge.name ?? edge.edgeId,
          location_id: targetLocationId,
          device: { manufacturer: edge.device?.manufacturer || "", model: edge.device?.model || "", firmware: edge.device?.firmware || "" },
          asset: { assetId: edge.asset?.assetId || "", type: edge.asset?.type || "", location: edge.asset?.location || "" },
          cloud_transfer_enabled: Boolean(edge.cloud_transfer_enabled),
          tier: edge.tier || "pro",
        };
        updated = await updateEdge(accessToken, edge.id, payload);
      }
      toast.success(t("edges.moveDialog.moved"));
      onSaved?.(updated);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("edges.moveDialog.failed"));
    } finally {
      setSubmitting(false);
    }
  };

  if (!edge) return null;

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { backgroundColor: "var(--bg-card)", color: "var(--text-primary)", border: "1px solid var(--border)" } }}>
      <DialogTitle sx={{ borderBottom: "1px solid var(--border)" }}>{t("edges.moveDialog.title")}</DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        <TextField
          select
          fullWidth
          margin="dense"
          label={t("edges.moveDialog.target")}
          value={targetLocationId}
          onChange={(e) => setTargetLocationId(e.target.value)}
          disabled={loadingLocations}
          helperText={loadingLocations ? t("edges.edit.locationsLoading") : undefined}
        >
          <MenuItem value={NO_LOCATION}>{t("edges.tree.noLocation")}</MenuItem>
          {locations.map((loc) => (
            <MenuItem key={loc.id} value={loc.id}>
              {loc.name} {loc.address ? `- ${loc.address}` : ""}
            </MenuItem>
          ))}
        </TextField>
      </DialogContent>
      <DialogActions sx={{ borderTop: "1px solid var(--border)", p: 2 }}>
        <Button onClick={onClose} disabled={submitting} sx={{ color: "var(--text-muted)" }}>{t("edges.cancel")}</Button>
        <Button onClick={handleMove} disabled={submitting} variant="contained" sx={{ backgroundColor: "var(--btn-primary)" }}>
          {submitting ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : t("edges.move")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
